export type WebAppInstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface BeforeInstallPromptChoice {
  outcome: 'accepted' | 'dismissed';
  platform: string;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
}

type InstallListener = () => void;

let captureStarted = false;
let removeCapture: (() => void) | null = null;
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installAvailable = false;
let installed = false;
const listeners = new Set<InstallListener>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function isBeforeInstallPromptEvent(
  event: Event,
): event is BeforeInstallPromptEvent {
  if (!('prompt' in event) || !('userChoice' in event)) {
    return false;
  }
  const candidate = event as {
    prompt?: unknown;
    userChoice?: unknown;
  };
  return typeof candidate.prompt === 'function' && candidate.userChoice != null;
}

/** True when this window is the installed web app, not a browser tab. */
export function isRunningInstalledWebApp(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) {
    return true;
  }
  if (typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(display-mode: standalone)').matches;
}

function markInstalled(): void {
  const changed = !installed || installAvailable || deferredPrompt !== null;
  installed = true;
  installAvailable = false;
  deferredPrompt = null;
  if (changed) {
    emit();
  }
}

export function startWebAppInstallPromptCapture(): void {
  if (captureStarted || typeof window === 'undefined') {
    return;
  }
  captureStarted = true;
  if (isRunningInstalledWebApp()) {
    installed = true;
  }

  const onBeforeInstallPrompt = (event: Event) => {
    if (!isBeforeInstallPromptEvent(event)) {
      return;
    }
    event.preventDefault();
    if (isRunningInstalledWebApp()) {
      markInstalled();
      return;
    }
    deferredPrompt = event;
    installAvailable = true;
    emit();
  };

  const onAppInstalled = () => {
    markInstalled();
  };

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);

  let removeMedia: (() => void) | undefined;
  if (typeof window.matchMedia === 'function') {
    const media = window.matchMedia('(display-mode: standalone)');
    const onChange = () => {
      if (isRunningInstalledWebApp()) {
        markInstalled();
      }
    };
    media.addEventListener('change', onChange);
    removeMedia = () => media.removeEventListener('change', onChange);
  }

  removeCapture = () => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
    removeMedia?.();
  };
}

export function subscribeWebAppInstallPrompt(
  listener: InstallListener,
): () => void {
  startWebAppInstallPromptCapture();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getWebAppInstallCanInstall(): boolean {
  startWebAppInstallPromptCapture();
  return installAvailable && !installed;
}

export function getWebAppInstallCanInstallServer(): boolean {
  return false;
}

/**
 * Opens the browser install prompt. Must be called from a click handler.
 * Dismissing the prompt leaves the app eligible so the button stays visible.
 * The deferred event can only be used once; the next `beforeinstallprompt`
 * supplies a new one.
 */
export function promptWebAppInstall(): Promise<WebAppInstallOutcome> {
  const event = deferredPrompt;
  if (!event || installed) {
    return Promise.resolve('unavailable');
  }
  deferredPrompt = null;

  let promptPromise: Promise<void>;
  try {
    promptPromise = event.prompt();
  } catch {
    return Promise.resolve('unavailable');
  }

  return promptPromise
    .then(() => event.userChoice)
    .then((choice) => {
      if (installed || choice.outcome === 'accepted') {
        markInstalled();
        return 'accepted' as const;
      }
      emit();
      return 'dismissed' as const;
    })
    .catch(() => 'unavailable' as const);
}

export function resetWebAppInstallPromptForTests(): void {
  removeCapture?.();
  removeCapture = null;
  captureStarted = false;
  deferredPrompt = null;
  installAvailable = false;
  installed = false;
  listeners.clear();
}
